import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useTranslation } from 'react-i18next';
import LeagueMap from './LeagueMap';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface League {
  id: number;
  name: string;
  description: string;
  venue: string;
  city: string;
  country: string;
  teamFormat: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  createdAt: string;
  adminId?: number;
  adminName?: string;
  alreadyJoinedSA?: boolean;
  packages: Package[];
  squads: Squad[];
  sideEvents: SideEvent[];
}

interface Package {
  id: number;
  name: string;
  description: string;
  price: number;
  includes: number[]; // Squad IDs
}

interface Squad {
  id: number;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  gamesPerSquad: number;
}

interface SideEvent {
  id: number;
  name: string;
  description: string;
  type: string;
  cost: number;
}

const BrowseLeagues: React.FC = () => {
 
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccessPremiumFeatures } = useSubscription();

  const [leagues, setLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isProcessingSubscription, setIsProcessingSubscription] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [sideActionModal, setSideActionModal] = useState<League | null>(null);
  const [joiningLeagueId, setJoiningLeagueId] = useState<number | null>(null);
  const [joinSuccess, setJoinSuccess] = useState<number | null>(null); // league id that was just joined

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const response = await fetch(`${apiUrl}/leagues/browse`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
         
          setLeagues(data);
        }
      } catch (error) {
        console.error('Failed to fetch leagues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeagues();
  }, []);

  const getLeagueStatusRaw = (league: League) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
    
    const startDate = new Date(league.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(league.endDate);
    endDate.setHours(23, 59, 59, 999); // End of day

    if (!league.isActive) return 'inactive';
    if (now < startDate) return 'upcoming';
    if (now > endDate) return 'completed';
    return 'active';
  };

  const getLeagueStatus = (league: League) => {
    const status = getLeagueStatusRaw(league);
    switch (status) {
      case 'inactive': return t('league.statusInactive');
      case 'upcoming': return t('league.statusUpcoming');
      case 'completed': return t('league.statusCompleted');
      case 'active': return t('league.statusActive');
      default: return t('league.statusActive');
    }
  };

  const getStatusColor = (statusRaw: string) => {
    switch (statusRaw) {
      case 'active': return 'bg-green-500/20 text-green-200 border-green-400';
      case 'upcoming': return 'bg-blue-500/20 text-blue-200 border-blue-400';
      case 'completed': return 'bg-gray-500/20 text-gray-200 border-gray-400';
      case 'inactive': return 'bg-red-500/20 text-red-200 border-red-400';
      default: return 'bg-purple-500/20 text-purple-200 border-purple-400';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const filteredLeagues = leagues.filter(league => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (league.name || '').toLowerCase().includes(term) ||
      (league.venue || '').toLowerCase().includes(term) ||
      (league.city || '').toLowerCase().includes(term) ||
      (league.description || '').toLowerCase().includes(term);
    
    const matchesCountry = selectedCountry === '' || league.country === selectedCountry;
    
    const leagueStatusRaw = getLeagueStatusRaw(league);
    const matchesStatus = selectedStatus === '' || leagueStatusRaw === selectedStatus;
    
    return matchesSearch && matchesCountry && matchesStatus;
  });


  // Get unique countries from leagues
  const availableCountries = Array.from(new Set(leagues.map(league => league.country)))
    .filter(country => country && country.trim() !== '')
    .sort();

  const isSideActionOnly = (league: League) =>
    league.description?.trim() === 'Side Action Only';

  const handleLeagueClick = (league: League) => {
    if (isSideActionOnly(league)) {
      setSideActionModal(league);
      return;
    }
    if (!canAccessPremiumFeatures) {
      setShowSubscriptionModal(true);
      return;
    }
    navigate(`/player/leagues/browse/${league.id}`);
  };

  const handleJoinSideAction = async () => {
    if (!sideActionModal) return;
    setJoiningLeagueId(sideActionModal.id);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/player/leagues/${sideActionModal.id}/join-side-action`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setJoinSuccess(sideActionModal.id);
        // Optimistically mark the league as joined in local state
        setLeagues(prev => prev.map(l =>
          l.id === sideActionModal!.id ? { ...l, alreadyJoinedSA: true } : l
        ));
        setSideActionModal(null);
      } else {
        const data = await res.json();
        alert(data.error || 'Could not join league. Please try again.');
      }
    } catch {
      alert('Network error. Please try again.');
    } finally {
      setJoiningLeagueId(null);
    }
  };

  const handleSubscribe = async () => {
    if (!user) return;

    setIsProcessingSubscription(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/subscription/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          playerId: user.id,
          playerEmail: user.email,
          playerName: user.name
        })
      });

      const data = await response.json();

      if (data.success && data.stripeUrl) {
        // Redirect to Stripe checkout
        window.location.href = data.stripeUrl;
      } else {
        console.error('Subscription error:', data.error);
        alert(data.error || t('messages.subscriptionFailed'));
      }
    } catch (error) {
      console.error('Failed to process subscription:', error);
      alert(t('messages.subscriptionFailed'));
    } finally {
      setIsProcessingSubscription(false);
      setShowSubscriptionModal(false);
    }
  };

  if (!user) {
    navigate('/player/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">{t('player.browseLeaguesTitle')}</h1>
              <p className="text-blue-200 mt-1">
                {t('player.discoverLeagues')}
              </p>
            </div>
            <button
              onClick={() => navigate('/player/dashboard')}
              className="px-4 py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition duration-200"
            >
              {t('player.backToDashboard')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder={t('player.searchLeaguesPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="lg:w-64">
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="" className="bg-gray-800 text-white">🌍 {t('player.allCountries')}</option>
                {availableCountries.map((country) => (
                  <option key={country} value={country} className="bg-gray-800 text-white">
                    {country}
                  </option>
                ))}
              </select>
            </div>
            <div className="lg:w-64">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em'
                }}
              >
                <option value="" className="bg-gray-800 text-white">📊 {t('player.allStatus')}</option>
                <option value="active" className="bg-gray-800 text-white">✅ {t('league.statusActive')}</option>
                <option value="upcoming" className="bg-gray-800 text-white">🔜 {t('league.statusUpcoming')}</option>
                <option value="completed" className="bg-gray-800 text-white">✔️ {t('league.statusCompleted')}</option>
              </select>
            </div>
            {(searchTerm || selectedCountry || selectedStatus) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCountry('');
                  setSelectedStatus('');
                }}
                className="px-4 py-2 bg-red-500/20 border border-red-400/30 text-red-200 rounded-lg hover:bg-red-500/30 transition duration-200 whitespace-nowrap"
              >
                {t('player.clearFilters')}
              </button>
            )}
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/20">
            <div className="text-blue-200 text-sm">
              {t('player.leaguesFound', { count: filteredLeagues.length })}
            </div>
            <div className="flex items-center gap-2 bg-white/5 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-md transition duration-200 flex items-center gap-2 ${
                  viewMode === 'list'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'text-blue-200 hover:bg-white/10'
                }`}
              >
                <span className="text-lg">📋</span>
                <span className="font-medium">{t('player.listView')}</span>
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-4 py-2 rounded-md transition duration-200 flex items-center gap-2 ${
                  viewMode === 'map'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'text-blue-200 hover:bg-white/10'
                }`}
              >
                <span className="text-lg">🗺️</span>
                <span className="font-medium">{t('player.mapView')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Leagues Display - List or Map View */}
        {viewMode === 'map' ? (
          <LeagueMap
            leagues={filteredLeagues}
            onLeagueClick={handleLeagueClick}
            formatDate={formatDate}
            getLeagueStatus={getLeagueStatus}
            t={t}
          />
        ) : loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-blue-200 text-lg">{t('player.loadingLeagues')}</p>
          </div>
        ) : filteredLeagues.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎳</div>
            <h3 className="text-xl font-bold text-white mb-2">
              {searchTerm ? t('player.noLeaguesFound') : t('player.noLeaguesAvailable')}
            </h3>
            <p className="text-blue-200 mb-6">
              {searchTerm 
                ? t('player.tryAdjustingSearch')
                : t('player.checkBackSoon')}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLeagues.map((league) => {
              const isSA = isSideActionOnly(league);
              const alreadyJoined = league.alreadyJoinedSA || joinSuccess === league.id;
              return (
              <div
                key={league.id}
                className={`backdrop-blur-lg rounded-xl p-6 border transition duration-300 cursor-pointer group ${
                  isSA
                    ? 'bg-amber-900/30 border-amber-400/50 hover:bg-amber-800/40'
                    : 'bg-white/10 border-white/20 hover:bg-white/15'
                }`}
                onClick={() => handleLeagueClick(league)}
              >
                {/* League Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1 group-hover:text-blue-200 transition">
                      {league.name}
                    </h3>
                    <p className="text-blue-300 text-sm">
                      {league.venue}, {league.city}
                      {league.country && (
                        <span className="ml-2">
                          {league.country}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs border ${getStatusColor(getLeagueStatusRaw(league))}`}>
                    {getLeagueStatus(league)}
                  </div>
                </div>

                {/* League Details */}
                <div className="space-y-2 text-sm text-blue-200 mb-4">
                  <div className="flex justify-between">
                    <span>{t('league.format')}:</span>
                    <span className="text-white capitalize">{league.teamFormat}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('league.startDate')}:</span>
                    <span className="text-white">{formatDate(league.startDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('league.endDate')}:</span>
                    <span className="text-white">{formatDate(league.endDate)}</span>
                  </div>
                </div>

                {/* Packages Preview */}
                {league.packages && league.packages.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-white mb-2">{t('player.packagesAvailable')}:</h4>
                    <div className="space-y-1">
                      {league.packages.slice(0, 2).map((pkg) => (
                        <div key={pkg.id} className="flex justify-between text-xs text-blue-300">
                          <span>{pkg.name}</span>
                          <span>${pkg.price}</span>
                        </div>
                      ))}
                      {league.packages.length > 2 && (
                        <p className="text-xs text-blue-400">{t('player.morePackages', { count: league.packages.length - 2 })}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Schedule Preview */}
                {league.squads && league.squads.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-white mb-2">{t('player.schedule')}:</h4>
                    <div className="space-y-1">
                      {league.squads.slice(0, 2).map((squad) => (
                        <div key={squad.id} className="text-xs text-blue-300">
                          <span>{squad.name}: {formatDate(squad.date)} </span>
                          <span>{formatTime(squad.startTime)}</span>
                        </div>
                      ))}
                      {league.squads.length > 2 && (
                        <p className="text-xs text-blue-400">{t('player.moreSessions', { count: league.squads.length - 2 })}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* League Description */}
                {league.description && (
                  <div className={`mb-4 pt-2 border-t ${isSA ? 'border-amber-400/30' : 'border-white/10'}`}>
                    {isSA ? (
                      <p className="text-amber-300 text-sm font-semibold tracking-wide uppercase">
                        🎯 {league.description}
                      </p>
                    ) : (
                      <p className="text-blue-200 text-sm line-clamp-2">
                        {league.description}
                      </p>
                    )}
                  </div>
                )}

                {/* Action Hint */}
                <div className={`pt-2 border-t ${isSA ? 'border-amber-400/30' : 'border-white/10'}`}>
                  <p className={`text-xs text-center transition ${
                    isSA
                      ? 'text-amber-300 group-hover:text-amber-200'
                      : 'text-blue-300 group-hover:text-blue-200'
                  }`}>
                    {isSA
                      ? (alreadyJoined ? '✅ Already in this side action' : '🎯 Click to join this side action')
                      : (canAccessPremiumFeatures
                          ? t('player.clickToViewDetails')
                          : t('player.upgradeToPremiumToRegister'))
                    }
                  </p>
                </div>
              </div>
            ); })}
          </div>
        )}
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-purple-800 rounded-xl p-8 border border-purple-400 max-w-md w-full">
            <div className="text-center">
              <div className="text-6xl mb-4">👑</div>
              <h3 className="text-2xl font-bold text-white mb-4">{t('player.upgradeToPremium')}</h3>
              <p className="text-purple-200 mb-6">
                {t('player.subscriptionPrice')} <span className="text-3xl font-bold text-yellow-300">$30 USD</span> {t('player.perYear')}
              </p>
              <ul className="text-left text-purple-200 mb-6 space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">🎳</span>
                  <span>{t('player.subscriptionBenefit1')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📊</span>
                  <span>{t('player.subscriptionBenefit2')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">📅</span>
                  <span>{t('player.subscriptionBenefit3')}</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🏆</span>
                  <span>{t('player.subscriptionBenefit4')}</span>
                </li>
              </ul>
              <div className="flex space-x-4">
                <button
                  onClick={handleSubscribe}
                  disabled={isProcessingSubscription}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-yellow-900 font-bold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessingSubscription ? t('player.processing') : t('player.subscribeNow')}
                </button>
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  disabled={isProcessingSubscription}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Side Action Join Modal */}
      {sideActionModal && (() => {
        const modalAlreadyJoined = sideActionModal.alreadyJoinedSA || joinSuccess === sideActionModal.id;
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-gradient-to-br from-amber-900 to-amber-800 rounded-xl p-8 border border-amber-400 max-w-md w-full">
              <div className="text-center">
                {modalAlreadyJoined ? (
                  <>
                    <div className="text-6xl mb-4">✅</div>
                    <h3 className="text-2xl font-bold text-white mb-3">Already Joined</h3>
                    <p className="text-amber-200 mb-6">
                      You are already a member of{' '}
                      <span className="font-bold text-white">{sideActionModal.name}</span>'s side action
                      {sideActionModal.adminName && (
                        <> organized by{' '}
                          <span className="font-bold text-amber-300">{sideActionModal.adminName}</span>
                        </>
                      )}.
                      <br />
                      <span className="text-sm text-amber-300 mt-2 block">
                        You can find it in your dashboard under "My Side Action Leagues".
                      </span>
                    </p>
                    <div className="flex space-x-4">
                      <button
                        onClick={() => { setSideActionModal(null); navigate('/player/dashboard'); }}
                        className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-amber-900 font-bold py-3 px-6 rounded-lg transition duration-200"
                      >
                        Go to Dashboard
                      </button>
                      <button
                        onClick={() => setSideActionModal(null)}
                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
                      >
                        Close
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-6xl mb-4">🎯</div>
                    <h3 className="text-2xl font-bold text-white mb-3">Join Side Action</h3>
                    <p className="text-amber-200 mb-6">
                      Do you wish to add yourself to{' '}
                      <span className="font-bold text-white">{sideActionModal.name}</span>
                      {sideActionModal.adminName && (
                        <> with side action organized by{' '}
                          <span className="font-bold text-amber-300">{sideActionModal.adminName}</span>
                        </>
                      )}
                      ?
                      <br />
                      <span className="text-sm text-amber-300 mt-2 block">
                        This will display your name on their league group.
                      </span>
                    </p>
                    <div className="flex space-x-4">
                      <button
                        onClick={handleJoinSideAction}
                        disabled={joiningLeagueId === sideActionModal.id}
                        className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-amber-900 font-bold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {joiningLeagueId === sideActionModal.id ? 'Joining…' : 'Yes, Join'}
                      </button>
                      <button
                        onClick={() => setSideActionModal(null)}
                        disabled={joiningLeagueId === sideActionModal.id}
                        className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BrowseLeagues;